import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderFieldForm, renderTextArea } from '../../shared/formComponents';
import { LANGUAGE_OPTIONS } from '../../shared/constants';
import GeoTargetingFields from './shared/GeoTargetingFields';

/**
 * TikTok Budget Fields Component
 * Handles all 3 TikTok ad types: Smart+ Catalog, In-Feed, TikTok Shop
 */
const TikTokBudgetFields = ({
  budgetTypeOption,
  setupFlow,
  agencyInternal,
  formValues,
  itemCsvInput,
}) => {
  if (!budgetTypeOption) return null;

  const { value: adType, fields } = budgetTypeOption;

  // URL validation helper
  const isValidUrl = (urlString) => {
    if (!urlString) return null; // null = no validation shown
    try {
      const url = new URL(urlString.trim());
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Product ID validation helper (basic - not null, no spaces)
  const validateProductIds = (idString) => {
    if (!idString) return { valid: true, ids: [], hasSpaces: false };
    const ids = idString.split(',').map(item => item.trim()).filter(item => item);
    const hasSpaces = ids.some(id => id.includes(' '));
    return {
      valid: ids.length > 0 && !hasSpaces,
      ids,
      count: ids.length,
      hasSpaces,
    };
  };

  // Get validation results
  const urlValidation = fields?.url ? isValidUrl(formValues?.masterItemOnList) : null;
  const productIdValidation = fields?.productIds ? validateProductIds(formValues?.masterItemOnList) : null;

  // Get display name for the ad type
  let adTypeDisplay = 'TikTok Shop';
  if (adType === 'tiktok_smart_catalog') {
    adTypeDisplay = 'Smart+ Catalog';
  } else if (adType === 'tiktok_infeed') {
    adTypeDisplay = 'In-Feed';
  }

  return (
    <>
      {/* URL Field for In-Feed */}
      {fields?.url && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Landing Page URL{' '}
            <Tooltip
              id="tiktokUrl"
              msg={`Enter the landing page URL for your ${adTypeDisplay} ads`}
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOnList"
              component={renderFieldForm}
              placeholder="https://..."
            />
          </div>
          {formValues?.masterItemOnList && urlValidation !== null && (
            <div
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: '4px',
                border: `1px solid ${urlValidation ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`,
                backgroundColor: `${urlValidation ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 0, 0, 0.1)'}`,
              }}
            >
              <div style={{ fontWeight: '500', color: urlValidation ? '#91BE23' : '#ff4861' }}>
                {urlValidation ? '✓ Valid URL' : '⚠ Invalid URL'}
              </div>
              {!urlValidation && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  URL must start with http:// or https://
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Product IDs Field for Smart+ Catalog and TikTok Shop */}
      {fields?.productIds && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Product Item IDs{' '}
            <Tooltip
              id="tiktokProductIds"
              msg={`Enter comma-separated ${adTypeDisplay} product IDs`}
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOnList"
              component={renderTextArea}
              format={itemCsvInput}
              placeholder="product_123, product_456, product_789"
            />
          </div>
          {formValues?.masterItemOnList && productIdValidation && (
            <div
              style={{
                marginTop: '10px',
                padding: '10px',
                borderRadius: '4px',
                border: `1px solid ${productIdValidation.valid ? 'rgba(145, 190, 35, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`,
                backgroundColor: `${productIdValidation.valid ? 'rgba(145, 190, 35, 0.1)' : 'rgba(255, 0, 0, 0.1)'}`,
              }}
            >
              <div style={{ fontWeight: '500', color: productIdValidation.valid ? '#91BE23' : '#ff4861' }}>
                {productIdValidation.valid ? '✓' : '⚠'} Product ID Count: {productIdValidation.count}
              </div>
              {productIdValidation.hasSpaces && (
                <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                  Product IDs cannot contain spaces
                </div>
              )}
              {productIdValidation.valid && productIdValidation.count > 0 && (
                <div style={{ fontSize: '0.9em', color: '#4a5568', marginTop: '4px' }}>
                  {productIdValidation.count} product {productIdValidation.count === 1 ? 'ID' : 'IDs'} entered
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Languages Multi-Select - All TikTok ad types */}
      <div className="form__form-group">
        <span className="form__form-group-label">
          Languages{' '}
          <Tooltip
            id="tiktokLanguages"
            msg="Select the languages to target for your TikTok ads"
            placement="top"
          />
        </span>
        <div className="form__form-group-field">
          <Field
            name="tiktokLanguages"
            component={renderSelectField}
            options={LANGUAGE_OPTIONS}
            isMulti
          />
        </div>
      </div>

      {/* Geo Targeting - Countries only (no states/cities/postal codes for TikTok) */}
      <GeoTargetingFields
        setupFlow={setupFlow}
        agencyInternal={agencyInternal}
        platform="tiktok"
        supportsStates={false}
        supportsCities={false}
        supportsPostalCodes={false}
      />
    </>
  );
};

TikTokBudgetFields.propTypes = {
  budgetTypeOption: PropTypes.shape({
    value: PropTypes.string,
    adType: PropTypes.string,
    itemType: PropTypes.string,
    fields: PropTypes.shape({
      url: PropTypes.bool,
      productIds: PropTypes.bool,
    }),
  }),
  setupFlow: PropTypes.shape({
    value: PropTypes.string,
  }),
  agencyInternal: PropTypes.shape({
    value: PropTypes.bool,
  }),
  formValues: PropTypes.shape({
    masterItemOnList: PropTypes.string,
    tiktokLanguages: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
    })),
  }),
  itemCsvInput: PropTypes.func.isRequired,
};

TikTokBudgetFields.defaultProps = {
  budgetTypeOption: null,
  setupFlow: null,
  agencyInternal: null,
  formValues: {},
};

export default TikTokBudgetFields;
